import Auth, { Token } from '@getgo/auth-client';
import React from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { AUTH_CLIENT_ID, AUTH_URL } from '../Constants';

const checkClientId = () => {
  let clientId = window.localStorage.getItem('clientId');
  if (!clientId) {
    clientId = uuidv4();
    window.localStorage.setItem('clientId', clientId);
  }
};

const checkHash = () => {
  let hashFilter = window.location.hash;
  const hashFilterFromStorage = window.localStorage.getItem('filter');
  if (!hashFilter && hashFilterFromStorage) {
    hashFilter = hashFilterFromStorage;
    if (history.pushState) {
      history.pushState(null, null, hashFilter);
    } else {
      location.hash = hashFilter;
    }
  } else {
    localStorage.setItem('filter', hashFilter);
  }
};

const getAuthOptions = (redirectUrl: string) => {
  return {
    client_id: AUTH_CLIENT_ID,
    url: AUTH_URL,
    redirect_url: redirectUrl,
    enableTokenRefresh: true,
    onTokenRefresh: (t: Token) => {
      localStorage.removeItem('accessToken');
      return Promise.resolve();
    },
    tokenRefreshTimestamp: (t: Token) => t.expires - 60000,
  };
};

export const checkLoginApp = (component: React.ReactElement, redirectUrl: string) => {
  checkClientId();
  const authOptions = getAuthOptions(redirectUrl);
  const auth = new Auth(authOptions);
  // A helper to get URL parameters
  const urlParams = new URLSearchParams(window.location.search);

  // We'll use sessionStorage to store temporary PKCE values
  const storage = window.sessionStorage;

  // Attempt to initialize with a token from storage (if you persist it)
  let token = auth.init(JSON.parse(localStorage.getItem('accessToken')));

  if (token) {
    // 1. A valid token was found. The user is already logged in.
    // Proceed with your application logic.
        checkHash();
        ReactDOM.render(component, document.getElementById('root'));
  } else {
    // No valid token exists. Check if this is a redirect back from the auth server.
    const stateFromLocation = urlParams.get('state');

    if (stateFromLocation) {
      // 2. Redirect back from the authorization server.
      // SECURITY CHECK: Verify the 'state' parameter to prevent CSRF attacks.
      if (storage.getItem('g2r_activation_portal_pkce_state') !== stateFromLocation) {
        throw new Error('State parameter mismatch. Possible session hijacking attack!');
      }

      // Get the authorization code from the URL.
      const authorizationCode = urlParams.get('code');
      if (!authorizationCode) {
        throw new Error('Authorization failed: No authorization code was returned.');
      }

      // Retrieve the original code_verifier from storage.
      const initialCodeVerifier = storage.getItem('g2r_activation_portal_pkce_code_verifier');

      // Exchange the authorization code for an access token.
      const config = {
        state: stateFromLocation,
        code: authorizationCode,
        code_verifier: initialCodeVerifier
      };
      
      // @ts-expect-error
      auth.requestPKCEToken(config)
        .then((token) => {
          localStorage.setItem('accessToken', JSON.stringify(token));

          // Clean up the URL and temporary storage
          storage.removeItem('g2r_activation_portal_pkce_state');
          storage.removeItem('g2r_activation_portal_pkce_code_verifier');
          const originalUrl = storage.getItem('g2r_activation_portal_pkce_original_url');
          storage.removeItem('g2r_activation_portal_pkce_original_url');

          location.replace(originalUrl);

        })
        .catch(() => {
          throw new Error('Authorization failed: Token request failed.');
        });
    } else {
      // 3. Initial visit. Start the login process.
      // Generate and store the state and code_verifier.
      storage.setItem('g2r_activation_portal_pkce_original_url', window.location.href);
      const state = auth.randomString(32);
      const code_verifier = auth.randomString(64);

      storage.setItem('g2r_activation_portal_pkce_state', state);
      storage.setItem('g2r_activation_portal_pkce_code_verifier', code_verifier);

      // Redirect the user to the login page.
      // @ts-expect-error
      auth.loginWithPKCE({ state, code_verifier });
    }
  }
};

export const logout = () => {
  checkClientId();
  const authOptions = getAuthOptions(window.location.origin);
  const auth = new Auth(authOptions);
  const accessToken = localStorage.getItem('accessToken');
  const token = accessToken ? auth.init(JSON.parse(accessToken)) : '';
  if (token) {
    localStorage.removeItem('accessToken');
    auth.logout();
    return;
  }
};
